import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, MapPin, Briefcase, Trash2 } from "lucide-react";
import {
  getSavedItems,
  unsaveListing,
  unsaveRequirement,
  SavedItem,
  SavedTargetType,
  ListingType,
  RequirementType,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { resolveImage } from "../../../Utils/helper";
import MarketplaceLoader from "./MarketplaceLoader";

import {
  PageWrapper,
  Container,
  PageHeader,
  PageTitle,
  PageSubtitle,
  FilterTabsRow,
  FilterTab,
  CountBadge,
  ResultsRow,
  ResultCount,
  SavedGrid,
  SavedCard,
  CardImageWrap,
  CardImage,
  CardImagePlaceholder,
  TypeBadge,
  UnavailableBadge,
  UnsaveBtn,
  CardBody,
  CardTitle,
  CardDesc,
  CardMeta,
  CardMetaItem,
  CardPrice,
  CardFooter,
  ViewBtn,
  RemoveBtn,
  ReqCard,
  ReqCardHeader,
  ReqCardBody,
  SkeletonCard,
  SkeletonBlock,
  EmptyState,
  EmptyEmoji,
  EmptyTitle,
  EmptyDesc,
  EmptyBtn,
  LoadMoreRow,
  LoadMoreBtn,
} from "./MarketplaceSavedPageStyles";

/* ─── Types ─── */
type TabKey = "all" | "equipment" | "services" | "jobs";

interface TabConfig {
  key: TabKey;
  label: string;
  targetType?: SavedTargetType;
  listingTypes?: ListingType[];
  requirementType?: RequirementType;
}

const TABS: TabConfig[] = [
  { key: "all",       label: "All Saved" },
  { key: "equipment", label: "Equipment",  targetType: "listing",     listingTypes: ["sale", "rent"] },
  { key: "services",  label: "Services",   targetType: "listing",     listingTypes: ["photographer", "videographer"] },
  { key: "jobs",      label: "Jobs",       targetType: "requirement" },
];

const TYPE_LABEL: Record<string, string> = {
  sale: "For Sale",
  rent: "Rent",
  photographer: "Photographer",
  videographer: "Videographer",
  equipment_requirement: "Equipment Need",
  service_requirement: "Service Need",
};

const EMOJI_BY_TYPE: Record<string, string> = {
  sale: "🎥",
  rent: "🔧",
  photographer: "📷",
  videographer: "🎬",
  equipment_requirement: "⚙️",
  service_requirement: "👤",
};

const PAGE_SIZE = 20;

/* ─── Skeleton ─── */
const SavedSkeleton: React.FC = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <SkeletonCard key={i}>
        <SkeletonBlock $h={150} $mb={0} style={{ borderRadius: 0 }} />
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <SkeletonBlock $h={18} $w="75%" $mb={4} />
          <SkeletonBlock $h={12} $w="100%" />
          <SkeletonBlock $h={12} $w="60%" />
          <SkeletonBlock $h={32} $mb={0} style={{ marginTop: 10 }} />
        </div>
      </SkeletonCard>
    ))}
  </>
);

/* ─── Main Component ─── */
const MarketplaceSavedPage: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [items, setItems] = useState<SavedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Auth guard
  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      navigate("/marketplace");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
    }
  }, [navigate]);

  const buildParams = useCallback((tab: TabKey, pg: number) => {
    const tabConfig = TABS.find(t => t.key === tab)!;
    const params: Record<string, any> = { page: pg, page_size: PAGE_SIZE };

    if (tab === "all") {
      // no filter
    } else if (tab === "equipment" || tab === "services") {
      params.target_type = "listing";
      // For multi-listing-type tabs, fetch separately (handled below)
    } else if (tab === "jobs") {
      params.target_type = "requirement";
    }
    return { params, tabConfig };
  }, []);

  const fetchSaved = useCallback(async (tab: TabKey, pg: number, append = false) => {
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const tabConfig = TABS.find(t => t.key === tab)!;

      let allResults: SavedItem[] = [];
      let count = 0;

      if (tab === "equipment" || tab === "services") {
        // Fetch each listing_type and merge
        const types = tabConfig.listingTypes!;
        const promises = types.map(lt =>
          getSavedItems({ target_type: "listing", listing_type: lt, page: pg, page_size: PAGE_SIZE })
        );
        const responses = await Promise.all(promises);
        responses.forEach(r => {
          allResults = [...allResults, ...(r.results ?? [])];
          count += r.count ?? 0;
        });
        // Sort by saved_at desc
        allResults.sort((a, b) => new Date(b.saved_at).getTime() - new Date(a.saved_at).getTime());
      } else {
        const { params } = buildParams(tab, pg);
        const res = await getSavedItems(params);
        allResults = res.results ?? [];
        count = res.count ?? 0;
        const hasNextPage = !!res.next;
        setHasMore(hasNextPage);
      }

      setTotalCount(count);
      setItems(prev => append ? [...prev, ...allResults] : allResults);
      if (tab !== "equipment" && tab !== "services") {
        setHasMore(allResults.length === PAGE_SIZE);
      } else {
        setHasMore(false); // for multi-type tabs, pagination handled per-type
      }
    } catch {
      if (!append) setItems([]);
      showErrorToast("Could not load saved items.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    fetchSaved(activeTab, 1);
  }, [activeTab, fetchSaved]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchSaved(activeTab, nextPage, true);
  };

  const handleUnsaveListing = async (e: React.MouseEvent, listingId: string, savedId: string) => {
    e.stopPropagation();
    try {
      await unsaveListing(listingId);
      setItems(prev => prev.filter(item => item.saved_id !== savedId));
      setTotalCount(prev => Math.max(0, prev - 1));
      showSuccessToast("Removed from saved items.");
    } catch {
      showErrorToast("Could not remove item. Please try again.");
    }
  };

  const handleUnsaveRequirement = async (e: React.MouseEvent, reqId: string, savedId: string) => {
    e.stopPropagation();
    try {
      await unsaveRequirement(reqId);
      setItems(prev => prev.filter(item => item.saved_id !== savedId));
      setTotalCount(prev => Math.max(0, prev - 1));
      showSuccessToast("Removed from saved items.");
    } catch {
      showErrorToast("Could not remove item. Please try again.");
    }
  };

  const renderListingCard = (item: SavedItem) => {
    if (!item.listing) return null;
    const l = item.listing;
    const isUnavailable = l.status !== "active";
    const imgSrc = l.cover_image || l.image_url || l.images?.[0]?.image_url;
    const typeLabel = TYPE_LABEL[l.listing_type] ?? l.listing_type;
    const emoji = EMOJI_BY_TYPE[l.listing_type] ?? "📦";

    return (
      <SavedCard
        key={item.saved_id}
        onClick={() => navigate(`/marketplace/listing/${l.id}`)}
        title={l.title}
      >
        <CardImageWrap>
          {imgSrc ? (
            <CardImage src={resolveImage(imgSrc)} alt={l.title} />
          ) : (
            <CardImagePlaceholder>{emoji}</CardImagePlaceholder>
          )}
          <TypeBadge $type={l.listing_type}>{typeLabel}</TypeBadge>
          {isUnavailable && (
            <UnavailableBadge><span>Unavailable</span></UnavailableBadge>
          )}
          <UnsaveBtn
            onClick={(e) => handleUnsaveListing(e, l.id, item.saved_id)}
            title="Remove from saved"
          >
            <Bookmark size={14} />
          </UnsaveBtn>
        </CardImageWrap>

        <CardBody>
          <CardTitle>{l.title}</CardTitle>
          <CardMeta>
            {l.location && (
              <CardMetaItem>
                <MapPin size={10} /> {l.location}
              </CardMetaItem>
            )}
          </CardMeta>
          {l.price && (
            <CardPrice>
              ₹{Number(l.price).toLocaleString("en-IN")}
              {l.listing_type === "rent" && <span>/ day</span>}
            </CardPrice>
          )}
        </CardBody>

        <CardFooter>
          <ViewBtn
            onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/listing/${l.id}`); }}
          >
            {isUnavailable ? "View Details" : l.listing_type === "rent" ? "Rent Now" : "Buy Now"}
          </ViewBtn>
          <RemoveBtn
            onClick={(e) => handleUnsaveListing(e, l.id, item.saved_id)}
          >
            <Trash2 size={12} /> Remove
          </RemoveBtn>
        </CardFooter>
      </SavedCard>
    );
  };

  const renderRequirementCard = (item: SavedItem) => {
    if (!item.requirement) return null;
    const r = item.requirement;
    const isUnavailable = r.status !== "active";
    const typeLabel = TYPE_LABEL[r.requirement_type] ?? r.requirement_type;

    return (
      <ReqCard key={item.saved_id}>
        <ReqCardHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <TypeBadge $type={r.requirement_type} style={{ position: "static" }}>
              {typeLabel}
            </TypeBadge>
            {isUnavailable && (
              <span style={{ fontSize: 9, fontFamily: "Montserrat, sans-serif", fontWeight: 700, color: "#ef4444", letterSpacing: 0.5 }}>
                ● UNAVAILABLE
              </span>
            )}
          </div>
          <button
            onClick={(e) => handleUnsaveRequirement(e, r.id, item.saved_id)}
            title="Remove from saved"
            style={{
              background: "linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%)",
              border: "none",
              borderRadius: "50%",
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(201,77,170,0.35)",
            }}
          >
            <Bookmark size={13} fill="#ffffff" />
          </button>
        </ReqCardHeader>

        <ReqCardBody>
          <CardTitle style={{ fontSize: 15 }}>{r.title}</CardTitle>
          {r.description && <CardDesc>{r.description}</CardDesc>}

          <CardMeta>
            {r.location && (
              <CardMetaItem>
                <MapPin size={10} /> {r.location}
              </CardMetaItem>
            )}
            {r.budget && (
              <CardMetaItem>
                <Briefcase size={10} /> ₹{r.budget}
              </CardMetaItem>
            )}
          </CardMeta>

          <div style={{ marginTop: "auto", paddingTop: 8 }}>
            <RemoveBtn
              style={{ width: "100%", justifyContent: "center" }}
              onClick={(e) => handleUnsaveRequirement(e, r.id, item.saved_id)}
            >
              <Trash2 size={12} /> Remove from Saved
            </RemoveBtn>
          </div>
        </ReqCardBody>
      </ReqCard>
    );
  };

  if (loading) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>
            My <span>Saved</span> Items
          </PageTitle>
          <PageSubtitle>
            Your personal wishlist — revisit saved listings, services, and jobs anytime.
          </PageSubtitle>
        </PageHeader>

        {/* ── FILTER TABS ── */}
        <FilterTabsRow>
          {TABS.map(tab => (
            <FilterTab
              key={tab.key}
              $active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              id={`saved-tab-${tab.key}`}
            >
              {tab.label}
              {activeTab === tab.key && !loading && (
                <CountBadge>{totalCount}</CountBadge>
              )}
            </FilterTab>
          ))}
        </FilterTabsRow>

        {/* ── RESULTS COUNT ── */}
        {!loading && (
          <ResultsRow>
            <ResultCount>
              {totalCount === 0
                ? "No saved items"
                : `${totalCount} saved item${totalCount === 1 ? "" : "s"}`}
            </ResultCount>
          </ResultsRow>
        )}

        {/* ── GRID ── */}
        <SavedGrid>
          {items.length === 0 ? null : (
            items.map(item => {
              if (item.target_type === "listing") return renderListingCard(item);
              if (item.target_type === "requirement") return renderRequirementCard(item);
              return null;
            })
          )}
        </SavedGrid>

        {/* ── EMPTY STATE ── */}
        {!loading && items.length === 0 && (
          <EmptyState>
            <EmptyEmoji>🤍</EmptyEmoji>
            <EmptyTitle>Nothing saved yet</EmptyTitle>
            <EmptyDesc>
              Tap the heart icon on any listing, service or job to save it here for later.
            </EmptyDesc>
            <EmptyBtn onClick={() => navigate("/marketplace/equipment")}>
              Browse Equipment
            </EmptyBtn>
          </EmptyState>
        )}

        {/* ── LOAD MORE ── */}
        {!loading && hasMore && (
          <LoadMoreRow>
            <LoadMoreBtn onClick={handleLoadMore} disabled={loadingMore} id="saved-load-more">
              {loadingMore ? "Loading…" : "Load More"}
            </LoadMoreBtn>
          </LoadMoreRow>
        )}
      </Container>
    </PageWrapper>
  );
};

export default MarketplaceSavedPage;
